// Language Context for i18n support
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Language, LanguageContextType } from '../types/language';
import { getTranslation } from '../utils/translations';

// Create language context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Language provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize language from localStorage or default to Vietnamese
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('kiosk-language');
    return (saved === 'vi' || saved === 'en') ? saved : 'vi';
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('kiosk-language', language);
  }, [language]);

  // Set language function
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
  };

  // Translation function
  const t = (key: string): string => {
    return getTranslation(language, key);
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Export context for testing
export { LanguageContext };